import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Synchronously syncs the document <head> for the given route.
 * Runs during render (browser only) so react-snap captures the tags
 * in the prerendered HTML — not after a useEffect tick.
 */
function syncHead(opts: Required<Pick<SEOProps, "title" | "description">> & {
  url: string;
  image: string;
  noindex?: boolean;
  jsonLd?: SEOProps["jsonLd"];
}) {
  if (typeof document === "undefined") return;

  document.title = opts.title;

  const setMeta = (attr: "name" | "property", key: string, value: string) => {
    let el = document.head.querySelector<HTMLMetaElement>(
      `meta[${attr}="${key}"]`,
    );
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", value);
  };

  setMeta("name", "description", opts.description);
  setMeta("name", "robots", opts.noindex ? "noindex, nofollow" : "index, follow");
  setMeta("property", "og:title", opts.title);
  setMeta("property", "og:description", opts.description);
  setMeta("property", "og:url", opts.url);
  setMeta("property", "og:image", opts.image);
  setMeta("property", "og:type", "website");
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", opts.title);
  setMeta("name", "twitter:description", opts.description);
  setMeta("name", "twitter:image", opts.image);

  let canonicalEl = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!canonicalEl) {
    canonicalEl = document.createElement("link");
    canonicalEl.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalEl);
  }
  canonicalEl.setAttribute("href", opts.url);

  // JSON-LD — remove any prior blocks we injected, then add fresh ones
  document
    .querySelectorAll('script[data-seo-json-ld="true"]')
    .forEach((el) => el.remove());
  if (opts.jsonLd) {
    const blocks = Array.isArray(opts.jsonLd) ? opts.jsonLd : [opts.jsonLd];
    blocks.forEach((block) => {
      const scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.dataset.seoJsonLd = "true";
      scriptEl.textContent = JSON.stringify(block);
      document.head.appendChild(scriptEl);
    });
  }
}

export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  noindex,
  jsonLd,
}: SEOProps) {
  const fullTitle = `Abba's House Communities | ${title}`;
  const image = ogImage ?? "https://images.abbashouse.org/assets/og-image.png";
  const url = canonical ?? "https://abbashouse.org/";

  // Synchronous: ensures prerender (react-snap) captures the tags.
  syncHead({ title: fullTitle, description, url, image, noindex, jsonLd });

  // Re-apply on prop changes during client-side navigation
  useEffect(() => {
    syncHead({ title: fullTitle, description, url, image, noindex, jsonLd });
  }, [fullTitle, description, url, image, noindex, jsonLd]);

  return null;
}
